// Portfolio dashboard at /portfolio-admin/portfolios/:portfolioId — the
// rollup view a loans-list group header / portfolio name links into.
// Header → stat tiles (v3 ledger sums + latest-DSCR average) →
// grade-distribution bar + 12-month collections trend (Phoenix income vs
// seller distributions from the WF1 allocation buckets — same mapping as
// the FR-M4 seller statement) → portfolio-scoped needs-attention strip
// (reuses the loan-level feed rows from the batch grades payload) → loans
// table with bulk "Move out". ALL metrics are server aggregates over the
// v3 ledger + the same batch health-grades the list uses — no parallel math.

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import {
  useLoansList,
  usePortfolioDashboard,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useLoanGrades } from "@/hooks/portfolioAdmin/useLoanHealth";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import {
  Button,
  Card,
  Skeleton,
  StatusBadge,
  useToast,
} from "@/design-system/components/ui";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { cn } from "@/design-system/lib/utils";
import {
  AttentionStrip,
  type AttentionChip,
} from "@/components/portfolioAdmin/loanDetail/common/AttentionStrip";
import { GradeDistributionBar } from "@/components/portfolioAdmin/loans/GradeDistributionBar";
import {
  LoansListTable,
  type LoanListRow,
} from "@/components/portfolioAdmin/loans/LoansListTable";
import { MoveToPortfolioModal } from "@/components/portfolioAdmin/portfolios/MoveToPortfolioModal";
import type { PortfolioMonthlyCollectionsDto } from "@/api/portfolioAdmin/types";

const MONTH_LABEL = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const DISCIPLINE_LABEL: Record<string, string> = {
  payments: "Payments",
  insurance: "Insurance",
  ucc: "UCC",
  covenants: "Covenants",
  reporting: "Reporting",
  vendors: "Vendors",
  qa: "QA",
  defaults: "Defaults",
  other: "Other",
};

export default function PortfolioDashboardPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Portfolio"
        description="Portfolio rollups."
      />
    );
  }
  return <PortfolioDashboardPageInner />;
}

function PortfolioDashboardPageInner() {
  const { tz } = useDateFormatter();
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const auth = useAuth();
  const toast = useToast();
  const dashboard = usePortfolioDashboard(portfolioId);
  const loansQuery = useLoansList(
    { portfolioIds: portfolioId ? [portfolioId] : undefined },
    !!portfolioId,
  );
  const loans = useMemo(() => loansQuery.data ?? [], [loansQuery.data]);
  const loanIds = useMemo(() => loans.map((l) => l.id), [loans]);
  const grades = useLoanGrades(loanIds);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [moveOutOpen, setMoveOutOpen] = useState(false);

  const canManage =
    auth.hasRoleCode("ADMIN") || auth.hasPermission("Loans.Manage");

  const gradeByLoanId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof grades.data>[number]>();
    for (const g of grades.data ?? []) map.set(g.loanId, g);
    return map;
  }, [grades.data]);

  const rows = useMemo<LoanListRow[]>(
    () =>
      loans.map((loan) => ({
        loan,
        grade: gradeByLoanId.get(loan.id),
        portfolioName: dashboard.data?.name ?? null,
      })),
    [loans, gradeByLoanId, dashboard.data?.name],
  );

  const gradeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const g of grades.data ?? []) {
      const letter = g.grade.grade;
      if (letter) counts[letter] = (counts[letter] ?? 0) + 1;
    }
    return counts;
  }, [grades.data]);

  // Portfolio-scoped needs-attention: the per-loan feed rows from the batch
  // payload, grouped by discipline, each item prefixed with its loan number.
  const attentionChips = useMemo<AttentionChip[]>(() => {
    const numberById = new Map(loans.map((l) => [l.id, l.loanNumber]));
    const byDiscipline = new Map<
      string,
      { tone: "error" | "warning"; items: AttentionChip["items"] }
    >();
    for (const g of grades.data ?? []) {
      for (const a of g.attention) {
        const entry =
          byDiscipline.get(a.discipline) ??
          ({ tone: "warning", items: [] } as {
            tone: "error" | "warning";
            items: AttentionChip["items"];
          });
        if (a.severity === "error") entry.tone = "error";
        entry.items.push({
          id: `${g.loanId}-${a.id}`,
          title: `${numberById.get(g.loanId) ?? "Loan"} — ${a.title}`,
          owner: a.ownerRole,
          date: a.dueDate,
        });
        byDiscipline.set(a.discipline, entry);
      }
    }
    return [...byDiscipline.entries()].map(([discipline, entry]) => ({
      id: discipline,
      label: DISCIPLINE_LABEL[discipline] ?? discipline,
      count: entry.items.length,
      tone: entry.tone,
      items: entry.items,
    }));
  }, [grades.data, loans]);

  if (!portfolioId) return null;

  if (dashboard.isLoading) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="portfolio-dashboard-loading">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (dashboard.isError || !dashboard.data) {
    return (
      <Card data-testid="portfolio-dashboard-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Couldn't load this portfolio.
        </p>
        <Button size="sm" variant="secondary" onClick={() => void dashboard.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  const d = dashboard.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="portfolio-dashboard">
      <div className="flex flex-col gap-space-1">
        <Link
          to="/portfolio-admin/portfolios"
          className="inline-flex items-center gap-1 text-caption text-gray-mid hover:text-onyx no-underline"
          data-testid="portfolio-back-link"
        >
          <ChevronLeft size={14} aria-hidden /> Portfolios
        </Link>
        <div className="flex items-baseline gap-space-3 flex-wrap">
          <h1 className="font-display font-bold text-h1 m-0" data-testid="portfolio-title">
            {d.name}
          </h1>
          <StatusBadge tone={d.status === "Open" ? "ok" : "neutral"}>{d.status}</StatusBadge>
          <span className="text-caption text-gray-mid">
            Created {formatDateInTz(d.createdAt, tz)}
          </span>
        </div>
        {d.description && (
          <p className="text-small text-gray-mid m-0">{d.description}</p>
        )}
      </div>

      <div
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-space-3"
        data-testid="portfolio-stat-tiles"
      >
        <StatTile label="Loans" testId="portfolio-tile-loans">
          <span className="text-h3 font-bold tabular-nums text-onyx">{d.loanCount}</span>
        </StatTile>
        <StatTile label="Total balance" testId="portfolio-tile-balance">
          <span className="text-h4 font-bold tabular-nums text-onyx">
            {formatCurrency(d.totalCurrentBalance)}
          </span>
        </StatTile>
        <StatTile label="Expected monthly income" testId="portfolio-tile-expected">
          <span className="text-h4 font-bold tabular-nums text-onyx">
            {formatCurrency(d.expectedMonthlyIncome)}
          </span>
        </StatTile>
        <StatTile label="Collected YTD" testId="portfolio-tile-collected">
          <span className="text-h4 font-bold tabular-nums text-onyx">
            {formatCurrency(d.collectedYearToDate)}
          </span>
        </StatTile>
        <StatTile label="Delinquent" testId="portfolio-tile-delinquent">
          <span
            className={cn(
              "text-h3 font-bold tabular-nums",
              d.delinquentCount > 0 ? "text-status-error" : "text-onyx",
            )}
          >
            {d.delinquentCount}
          </span>
        </StatTile>
        <StatTile label="Avg DSCR" testId="portfolio-tile-dscr">
          {d.averageDscr != null ? (
            <span className="text-h3 font-bold tabular-nums text-onyx">
              {d.averageDscr.toFixed(2)}
            </span>
          ) : (
            <span className="text-small text-gray-mid">None recorded</span>
          )}
        </StatTile>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-3">
        <Card data-testid="portfolio-grade-distribution">
          <SectionHeading>Grade distribution</SectionHeading>
          {grades.isLoading && loanIds.length > 0 ? (
            <Skeleton className="h-8 w-full" />
          ) : (
            <GradeDistributionBar
              gradeCounts={gradeCounts}
              testIdPrefix="portfolio-grade"
            />
          )}
        </Card>
        <Card data-testid="portfolio-collections-trend">
          <SectionHeading>Collections — last 12 months</SectionHeading>
          <CollectionsTrend months={d.monthlyCollections} />
        </Card>
      </div>

      {attentionChips.length > 0 && (
        <Card data-testid="portfolio-attention">
          <SectionHeading>Needs attention</SectionHeading>
          <AttentionStrip chips={attentionChips} testId="portfolio-attention-strip" />
        </Card>
      )}

      <div className="flex flex-col gap-space-2">
        <div className="flex items-center justify-between gap-space-3">
          <SectionHeading>Loans</SectionHeading>
          {canManage && selectedIds.length > 0 && (
            <div
              className="flex items-center gap-space-2 bg-gold-highlight/10 border border-gold-shadow rounded-md px-space-3 py-space-2"
              data-testid="portfolio-bulk-actions"
            >
              <span className="text-small font-semibold">{selectedIds.length} selected</span>
              <button
                type="button"
                className="text-small text-gold-shadow hover:underline"
                onClick={() => setMoveOutOpen(true)}
                data-testid="portfolio-bulk-move-out"
              >
                Move out
              </button>
            </div>
          )}
        </div>
        <LoansListTable
          rows={rows}
          gradesLoading={grades.isLoading && loanIds.length > 0}
          loading={loansQuery.isLoading}
          error={loansQuery.isError ? "Couldn't load this portfolio's loans." : null}
          selectable={canManage}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </div>

      {moveOutOpen && (
        <MoveToPortfolioModal
          loanIds={selectedIds}
          anyCurrentlyAssigned
          mode="remove"
          onClose={() => setMoveOutOpen(false)}
          onDone={(moved) => {
            setMoveOutOpen(false);
            setSelectedIds([]);
            toast.success(`Removed ${moved} ${moved === 1 ? "loan" : "loans"} from "${d.name}".`);
          }}
        />
      )}
    </div>
  );
}

/** CSS stacked bars — Phoenix income + seller payment + the unsplit
 *  remainder per month; native title tooltip carries the split. */
function CollectionsTrend({ months }: { months: PortfolioMonthlyCollectionsDto[] }) {
  const max = Math.max(...months.map((m) => m.total), 0);
  if (max === 0) {
    return (
      <p className="text-small text-gray-mid m-0" data-testid="portfolio-trend-empty">
        No collections in the last 12 months.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-space-2">
      <div className="flex items-end gap-space-1 h-28" role="img" aria-label="Monthly collections">
        {months.map((m) => {
          const label = `${MONTH_LABEL[m.month - 1]} ${m.year}`;
          const other = Math.max(m.total - m.phoenixIncome - m.sellerPayment, 0);
          const title = `${label} — total ${formatCurrency(m.total)} · Phoenix ${formatCurrency(m.phoenixIncome)} · Seller ${formatCurrency(m.sellerPayment)}`;
          return (
            <div
              key={`${m.year}-${m.month}`}
              className="flex-1 flex flex-col justify-end h-full"
              title={title}
              data-testid={`portfolio-trend-${m.year}-${m.month}`}
            >
              <div
                className="w-full flex flex-col justify-end overflow-hidden rounded-sm"
                style={{ height: `${(m.total / max) * 100}%` }}
              >
                <span className="w-full bg-gray-mid/40" style={{ flexGrow: other }} />
                <span className="w-full bg-status-info" style={{ flexGrow: m.sellerPayment }} />
                <span className="w-full bg-status-success" style={{ flexGrow: m.phoenixIncome }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-caption text-gray-mid">
        <span>{MONTH_LABEL[months[0].month - 1]} {months[0].year}</span>
        <span>
          {MONTH_LABEL[months[months.length - 1].month - 1]} {months[months.length - 1].year}
        </span>
      </div>
      <div className="flex gap-space-3 text-caption text-gray-mid">
        <LegendSwatch className="bg-status-success" label="Phoenix income" />
        <LegendSwatch className="bg-status-info" label="Seller distributions" />
        <LegendSwatch className="bg-gray-mid/40" label="Other / unsplit" />
      </div>
    </div>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-space-1">
      <span className={cn("h-2 w-2 rounded-sm inline-block", className)} aria-hidden />
      {label}
    </span>
  );
}

function StatTile({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="text-right" data-testid={testId}>
      <div className="text-caption text-gray-mid uppercase tracking-label mb-space-1 text-left">
        {label}
      </div>
      {children}
    </Card>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
      {children}
    </h2>
  );
}
